/**
 * Import executions (TO-413, Epic 25 F2).
 *
 * One import execution per physical file a tenant sends: the correlation id for
 * the file's whole import lifecycle. F2 drives it from discovery to `READY`
 * (quarantine + malware scan); F6–F9 drive the later statuses.
 */
import type { LogicalFileType, RetrievalMethodType } from './file-exchange.interface.js'
import type { ImportProcessingSummary } from './import-processing.js'
import type { ImportValidationSummary } from './import-validation.js'

/**
 * The lifecycle of one import execution. F2 implements and guards only the
 * transitions up to `READY`; the later members exist for F6–F9.
 * `SCAN_FAILED` is the "security scan failed" state — never treated as clean.
 */
export enum ImportExecutionStatus {
	DISCOVERED = 'DISCOVERED',
	RETRIEVING = 'RETRIEVING',
	RECEIVED = 'RECEIVED',
	QUARANTINED = 'QUARANTINED',
	SCANNING = 'SCANNING',
	READY = 'READY',
	PARSING = 'PARSING',
	TRANSFORMING = 'TRANSFORMING',
	VALIDATING = 'VALIDATING',
	PLANNING = 'PLANNING',
	PROCESSING = 'PROCESSING',
	COMPLETED = 'COMPLETED',
	RETRIEVAL_FAILED = 'RETRIEVAL_FAILED',
	SECURITY_REJECTED = 'SECURITY_REJECTED',
	SCAN_FAILED = 'SCAN_FAILED',
	PARSE_FAILED = 'PARSE_FAILED',
	TRANSFORMATION_FAILED = 'TRANSFORMATION_FAILED',
	VALIDATION_FAILED = 'VALIDATION_FAILED',
	PROCESSING_FAILED = 'PROCESSING_FAILED',
}

/**
 * The pipeline stage a log entry or alert refers to (TO-398 BR-19) — the
 * file-exchange pipeline's step, not the deployment stage.
 */
export enum ImportPipelineStage {
	DISCOVERY = 'discovery',
	RETRIEVAL = 'retrieval',
	RECEIPT = 'receipt',
	SCAN = 'scan',
	PARSE = 'parse',
	TRANSFORM = 'transform',
	VALIDATE = 'validate',
	PLAN = 'plan',
	PROCESS = 'process',
	/** The missing-file sweep checking a schedule's expectation. */
	EXPECTATION_CHECK = 'expectation_check',
}

/** A malware scanner's verdict on one object. Only `CLEAN` is clean. */
export enum MalwareScanVerdict {
	CLEAN = 'CLEAN',
	THREAT_FOUND = 'THREAT_FOUND',
	SCAN_FAILED = 'SCAN_FAILED',
}

/** What a receipt did: a new file, or one whose `(tenant, logical file, sha256)` already exists. */
export enum ImportReceiptOutcome {
	RECEIVED = 'received',
	DUPLICATE = 'duplicate',
}

/** Stable machine-readable codes of `ImportExecutionError`. */
export enum ImportExecutionErrorCode {
	INVALID = 'IMPORT_EXECUTION_INVALID',
	NOT_FOUND = 'IMPORT_EXECUTION_NOT_FOUND',
	INVALID_TRANSITION = 'IMPORT_EXECUTION_INVALID_TRANSITION',
	/** A scan result arrived before the file's receipt was reported; retryable. */
	NOT_RECEIVED = 'IMPORT_EXECUTION_NOT_RECEIVED',
	/** The import definition is missing or not ACTIVE; `details.reason` (`NOT_FOUND` | `NOT_ACTIVE`) (TO-414). */
	IMPORT_DEFINITION_UNUSABLE = 'IMPORT_DEFINITION_UNUSABLE',
	/** Validation needs status READY or VALIDATING; `details.status` (TO-396). */
	VALIDATION_NOT_ALLOWED = 'IMPORT_EXECUTION_VALIDATION_NOT_ALLOWED',
	/** A loader, mapper or business validator broke its contract; `details.port`, `details.reason` (TO-396). */
	VALIDATION_PORT_CONTRACT_BROKEN = 'IMPORT_VALIDATION_PORT_CONTRACT_BROKEN',
}

/** Why an execution failed, in `failureCode`, when the pipeline sets it itself (F2, F9). */
export enum ImportFailureCode {
	MALWARE_DETECTED = 'MALWARE_DETECTED',
	MALWARE_SCAN_FAILED = 'MALWARE_SCAN_FAILED',
	/** No domain importer is registered for the definition's canonical schema (TO-397 BR-9). */
	IMPORTER_NOT_REGISTERED = 'IMPORTER_NOT_REGISTERED',
	/** `onError: reject-file` and the plan had an invalid entry (TO-397 BR-7). */
	PLAN_REJECTED = 'PLAN_REJECTED',
	/** `atomic: true` and the importer refused the commit (TO-397 BR-8). */
	APPLY_REJECTED = 'APPLY_REJECTED',
	/** A stored `upsert`/`full_load` contract names no source system (TO-518 BR-F). */
	SOURCE_SYSTEM_REQUIRED = 'SOURCE_SYSTEM_REQUIRED',
}

/** Where a file came from. `connectorId` and `remotePath` are set for `sftp-pull` only. */
export interface ImportSource {
	transport: RetrievalMethodType
	connectorId: string | null
	remotePath: string | null
}

/** One import execution, as the service returns it. Timestamps are UTC; unset values are null. */
export interface ImportExecutionDTO {
	id: string
	tenantId: string
	logicalFile: LogicalFileType
	/** `key@version` of the import definition (format-checked; F5 adds existence). */
	importDefinition: string
	source: ImportSource
	originalFilename: string
	/** The bucket and key of the CURRENT object — consumers never build keys. */
	bucket: string
	objectKey: string
	sizeBytes: number | null
	sha256: string | null
	status: ImportExecutionStatus
	scanVerdict: MalwareScanVerdict | null
	failureCode: string | null
	createdBy: string | null
	createdAt: string
	updatedAt: string
	receivedAt: string | null
	scanCompletedAt: string | null
	readyAt: string | null
	readyEventPublishedAt: string | null
	processingClaimedAt: string | null
	/** Counts of the plan's entries by action (TO-397 BR-15); null until processed. */
	processingSummary: ImportProcessingSummary | null
	processedAt: string | null
	/** The bounded outcome of the last validation run (TO-396); null until validated. */
	validationSummary: ImportValidationSummary | null
}
